import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api.js";
import { PaymentIdentifiedScreen } from "../components/PaymentIdentifiedScreen.js";
import { PreparingAccessScreen } from "../components/PreparingAccessScreen.js";
import {
  completeIntegratedPaymentReturn,
  goToAutoLogin,
  isEmbeddedBillingContext,
} from "../lib/billingPostMessage.js";
import { delayAfterPaymentIdentified } from "../lib/paymentIdentifiedRedirect.js";
import { provisionAndGetLoginUrl } from "../lib/provisionAccess.js";
import { persistCheckoutOfferToken } from "../lib/checkoutOfferSession.js";
import { BILLING_SUPPORT_WHATSAPP_DISPLAY, BILLING_SUPPORT_WHATSAPP_URL } from "../lib/billingSupport.js";
import { clarityEvent, claritySet } from "../lib/clarity.js";
import { pollOrderUntilSettled, sleep } from "../lib/orderStatusPoll.js";
import type { BillingInterval, PaymentMethod, Plan } from "../../../shared/types.js";

interface OrderStatusResponse {
  status: string;
  zenpost_user_created: boolean;
  redirect: string | null;
  context_kind?: string;
  plan?: Plan;
  interval?: BillingInterval;
  payment_method?: PaymentMethod;
  app_return_url?: string | null;
  offer_already_redeemed?: boolean;
}

type PagePhase = "loading" | "identified" | "preparing" | "done";
/**
 * `pending`: paid (or still paying) without access yet, and support has been told.
 * `unconfirmed`: the page could not read the order or reach the server, so it claims neither.
 */
/**
 * `stoneInReview`: a Stone card charge the issuer is still reviewing (or Stone has not answered yet).
 * `stoneDeclined`: that charge came back declined.
 */
type DoneStatus =
  | "approved"
  | "pending"
  | "unconfirmed"
  | "integrated"
  | "offerAlreadyRedeemed"
  | "stoneInReview"
  | "stoneDeclined";

/** Clarity code for the message the buyer ended up reading. Saved filters use these names. */
const DONE_STATUS_CLARITY_CODES: Record<DoneStatus, string> = {
  approved: "approved",
  pending: "pending",
  unconfirmed: "unconfirmed",
  integrated: "integrated",
  offerAlreadyRedeemed: "offer_already_redeemed",
  stoneInReview: "stone_in_review",
  stoneDeclined: "stone_declined",
};

export function SuccessPage() {
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get("order");
  const sessionId = params.get("session_id");
  const skipIdentifiedDelay = params.get("identified") === "1";

  const [phase, setPhase] = useState<PagePhase>("loading");
  const [doneStatus, setDoneStatus] = useState<DoneStatus>("approved");
  const handledRef = useRef(false);

  useEffect(() => {
    persistCheckoutOfferToken(null);
  }, []);

  useEffect(() => {
    if (phase !== "done") return;
    const code = DONE_STATUS_CLARITY_CODES[doneStatus];
    clarityEvent(`success_shown_${code}`);
    claritySet("success_message", code);
  }, [phase, doneStatus]);

  useEffect(() => {
    if (!orderId || handledRef.current) return;
    handledRef.current = true;

    let cancelDelay: (() => void) | undefined;

    const afterIdentifiedScreen = (action: () => void) => {
      if (skipIdentifiedDelay) {
        action();
        setPhase("done");
        return;
      }
      setPhase("identified");
      cancelDelay = delayAfterPaymentIdentified(() => {
        action();
        setPhase("done");
      });
    };

    void (async () => {
      if (sessionId) {
        try {
          await api(`/api/public/orders/${orderId}/recheck`, { method: "POST" });
        } catch {
          /* webhook may have already processed */
        }
      }

      const readOrder = () => api<OrderStatusResponse>(`/api/public/orders/${orderId}`);
      const polled = await pollOrderUntilSettled({
        read: readOrder,
        sleep,
        onWaiting: () => setPhase("preparing"),
      });
      if (polled.reads > 1) clarityEvent("success_waited_for_payment");

      // The "support was notified" message is only true once the server has told support. It does
      // that once per order, so a second call (reload, or provisioning failing after a late payment)
      // is harmless.
      let pendingReported = false;
      const reportAccessPending = async () => {
        if (pendingReported) return null;
        try {
          const outcome = await api<{ status: string; alerted: boolean }>(
            `/api/public/orders/${orderId}/access-pending`,
            { method: "POST" },
          );
          pendingReported = outcome.status !== "paid";
          return outcome;
        } catch {
          pendingReported = true;
          return null;
        }
      };

      let latest = polled.order;
      if (!latest || latest.status !== "paid") {
        // Before saying access is not released, the server asks the provider once: the webhook may
        // have failed rather than be late.
        const outcome = await reportAccessPending();
        if (!outcome) {
          // Neither the order nor support could be confirmed: say so, and point to WhatsApp.
          setDoneStatus("unconfirmed");
          setPhase("done");
          return;
        }
        if (outcome.status === "paid") {
          clarityEvent("success_paid_after_provider_check");
          // The order is paid now: carry on into the access flow even if re-reading it keeps failing.
          const reread = await pollOrderUntilSettled({ read: readOrder, sleep, maxWaitMs: 4_000 });
          latest = reread.order ?? (latest ? { ...latest, status: "paid" } : null);
        }
      }
      if (!latest) {
        setDoneStatus("unconfirmed");
        setPhase("done");
        return;
      }
      const r = latest;

      // Second payment on a used offer link: no account is created for it and the charge is
      // refunded by hand, so skip the access flow and say so.
      if (r.offer_already_redeemed) {
        setDoneStatus("offerAlreadyRedeemed");
        setPhase("done");
        return;
      }

      // Stone charges in the checkout itself: an order that is not paid by now
      // is in the issuer's review, or was declined. Saying "approved" or "support
      // knows" would be wrong either way.
      if (r.payment_method === "stone" && r.status !== "paid") {
        setDoneStatus(r.status === "pending" ? "stoneInReview" : "stoneDeclined");
        setPhase("done");
        return;
      }

      const embedded = isEmbeddedBillingContext();

      // New customer: after the "payment identified" screen, provision the
      // account via n8n and auto-login the buyer into app.zenpost.ai.
      if (r.status === "paid" && r.context_kind === "new_customer") {
        const finishNewCustomer = async () => {
          setPhase("preparing");
          const loginUrl = (await provisionAndGetLoginUrl(orderId)) ?? r.redirect ?? null;
          if (loginUrl) {
            if (r.plan && r.interval) {
              goToAutoLogin({
                loginUrl,
                plan: r.plan,
                interval: r.interval,
                paymentMethod: r.payment_method,
              });
            } else if (!embedded) {
              window.location.href = loginUrl;
            }
            return; // navigating away (or parent will) — keep showing preparing
          }
          if (!r.zenpost_user_created) void reportAccessPending();
          setDoneStatus(r.zenpost_user_created ? "approved" : "pending");
          setPhase("done");
        };
        if (skipIdentifiedDelay) {
          void finishNewCustomer();
        } else {
          setPhase("identified");
          cancelDelay = delayAfterPaymentIdentified(() => {
            void finishNewCustomer();
          });
        }
        return;
      }

      if (r.status === "paid" && r.plan && r.interval && embedded) {
        afterIdentifiedScreen(() => {
          setDoneStatus(r.context_kind === "existing_customer" ? "integrated" : "approved");
          completeIntegratedPaymentReturn({
            plan: r.plan!,
            interval: r.interval!,
            paymentMethod: r.payment_method,
            actionDelayMs: 0,
          });
        });
        return;
      }

      if (r.redirect) {
        afterIdentifiedScreen(() => {
          window.location.href = r.redirect!;
        });
        return;
      }

      if (r.status === "paid" && r.context_kind === "existing_customer" && r.plan && r.interval) {
        afterIdentifiedScreen(() => {
          setDoneStatus("integrated");
          completeIntegratedPaymentReturn({
            plan: r.plan!,
            interval: r.interval!,
            paymentMethod: r.payment_method,
            appReturnUrl: r.app_return_url,
            actionDelayMs: 0,
          });
        });
        return;
      }

      if (r.status === "paid") {
        afterIdentifiedScreen(() => {
          if (!r.zenpost_user_created) void reportAccessPending();
          setDoneStatus(r.zenpost_user_created ? "approved" : "pending");
        });
        return;
      }

      // Still not paid after the wait and the provider check: support has been told above.
      setDoneStatus("pending");
      setPhase("done");
    })();

    return () => {
      cancelDelay?.();
    };
  }, [orderId, sessionId, skipIdentifiedDelay]);

  if (phase === "loading") {
    return (
      <main className="min-h-screen surface-aurora flex items-center justify-center p-4">
        <div className="card-glass max-w-md w-full p-8 text-center text-[color:var(--text-primary)]">
          <div className="text-sm text-ink-2">{t("common.loading")}</div>
        </div>
      </main>
    );
  }

  if (phase === "identified") {
    return <PaymentIdentifiedScreen />;
  }

  if (phase === "preparing") {
    return <PreparingAccessScreen />;
  }

  if (doneStatus === "offerAlreadyRedeemed") {
    return (
      <main className="min-h-screen surface-aurora flex items-center justify-center p-4">
        <div className="card-glass max-w-md w-full p-8 text-center text-[color:var(--text-primary)]">
          <div className="text-2xl font-semibold mb-3">{t("success.offerAlreadyRedeemedTitle")}</div>
          <p className="text-sm text-ink-2 leading-relaxed">{t("success.offerAlreadyRedeemedBody")}</p>
          <p className="text-sm text-ink-2 leading-relaxed mt-3">
            {t("success.offerAlreadyRedeemedSupport", { phone: BILLING_SUPPORT_WHATSAPP_DISPLAY })}
          </p>
          <a
            href={BILLING_SUPPORT_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-block mt-4"
          >
            {t("success.offerAlreadyRedeemedContact")}
          </a>
        </div>
      </main>
    );
  }

  if (doneStatus === "stoneInReview" || doneStatus === "stoneDeclined") {
    return (
      <main className="min-h-screen surface-aurora flex items-center justify-center p-4">
        <div className="card-glass max-w-md w-full p-8 text-center text-[color:var(--text-primary)]">
          <p className="text-base leading-relaxed">
            {doneStatus === "stoneInReview" ? t("success.stoneInReview") : t("success.stoneDeclined")}
          </p>
          {doneStatus === "stoneDeclined" && (
            <button type="button" className="btn-primary inline-block mt-4" onClick={() => window.history.back()}>
              {t("success.stoneDeclinedRetry")}
            </button>
          )}
        </div>
      </main>
    );
  }

  if (doneStatus === "unconfirmed") {
    return (
      <main className="min-h-screen surface-aurora flex items-center justify-center p-4">
        <div className="card-glass max-w-md w-full p-8 text-center text-[color:var(--text-primary)]">
          <p className="text-base leading-relaxed">{t("success.unconfirmed")}</p>
          <a
            href={BILLING_SUPPORT_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-block mt-4"
          >
            {t("success.unconfirmedContact")}
          </a>
        </div>
      </main>
    );
  }

  const title =
    doneStatus === "integrated"
      ? t("success.integratedTitle")
      : doneStatus === "approved"
        ? t("success.approved")
        : t("success.pending");

  return (
    <main className="min-h-screen surface-aurora flex items-center justify-center p-4">
      <div className="card-glass max-w-md w-full p-8 text-center text-[color:var(--text-primary)]">
        <div className="text-2xl font-semibold mb-3">{title}</div>
        {doneStatus !== "integrated" && doneStatus === "approved" && (
          <div className="text-sm text-ink-2">{t("success.redirecting")}</div>
        )}
      </div>
    </main>
  );
}
